// =====================================================================
// AdminRoute, wraps ProtectedRoute and additionally requires the
// `admin` role on the user's profile. Non-admin users fall through
// to /portal so they always land somewhere useful.
//
// Like ProtectedRoute, this owns its own loading UI now that the
// top-level App.jsx no longer renders a global boot screen.
// =====================================================================

import { Navigate, useLocation } from "react-router-dom";
import { useAuth } from "../context/AuthContext.jsx";
import Spinner from "./Spinner.jsx";

function BootScreen() {
  return (
    <div className="boot-screen" style={{ flexDirection: "column", gap: 18 }}>
      <img
        src="/icon.svg"
        alt=""
        width="36"
        height="36"
        style={{ opacity: 0.9 }}
      />
      <Spinner size={18} />
    </div>
  );
}

export default function AdminRoute({ children }) {
  const { isAuthenticated, isAdmin, loading, profileVerified } = useAuth();
  const location = useLocation();

  if (loading) {
    return <BootScreen />;
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace state={{ from: location }} />;
  }

  // Happy path: a trusted profile (cache or fresh network fetch)
  // already says admin, render immediately. No wait, no spinner.
  if (isAdmin) {
    return children;
  }

  // Profile currently says "not admin", but if it's the synthetic
  // placeholder we used while the network fetch was in flight,
  // redirecting now would punt a real admin to /portal. The user
  // would then see "the admin page didn't load" until they
  // refreshed and hit the warm cache. Wait for the fetch instead.
  //
  // profileVerified flips to true once the Supabase fetch completes
  // (success OR failure), or immediately when we have a cache hit.
  // Cache hit + role:user is honoured the moment it's read, so a
  // non-admin user trying /admin still gets a fast redirect.
  if (!profileVerified) {
    return <BootScreen />;
  }

  return <Navigate to="/portal" replace />;
}
